import React, { useCallback, useContext, useEffect, useState, forwardRef } from 'react';
import { observer } from "mobx-react-lite";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";

import { Context } from "../../../../index";
import { fetchAllPlaces } from "../../../../http/placesApi";
import {
    ABOUT_US_ROUTE,
    BLOG_ROUTE,
    MAIN_ROUTE,
    PHOTO_GALLERY_ROUTE,
    PLACES_ROUTE,
    PROFILE_ROUTE,
    SHOP_ROUTE
} from "../../../../utils/consts";

import PlaceNavItem from "../../../places/PlaceNavItem/PlaceNavItem";
import ErrorForm from "../../../modals/UserForms/ErrorForm/ErrorForm";
import FormSelection from "../../../modals/UserForms/FormSelection/FormSelection";
import { reachGoal } from "../../../../utils/metrika/metrika";
import ModalPortal from "../../../../utils/ModalPortal";

import burgerMenu from "../../../../Assets/utils/burger.svg";
import backArrow from "../../../../Assets/utils/backArrow.svg";

import styles from './Navigation.module.css';
import ExploreMenu from "../../MobileBottomNav/ExploreMenu/ExploreMenu";

const Navigation = observer(forwardRef((props, ref) => {
    const location = useLocation();
    const history = useHistory();
    const { id: profileIdParam } = useParams();

    const isFavouritesPage = location.pathname === '/favourites';
    const isMapSearchPage = location.pathname.startsWith('/map_search');

    const { add, helper, user } = useContext(Context);

    const [formSelectionVisible, setFormSelectionVisible] = useState(false);
    const [errorActive, setErrorActive] = useState(false);
    const [alertText, setAlertText] = useState('');
    const [activePath, setActivePath] = useState('');
    const [isExploreOpen, setIsExploreOpen] = useState(false);

    const [isPlacesMenuOpen, setIsPlacesMenuOpen] = useState(false);
    const [placesMenuCategory, setPlacesMenuCategory] = useState('architecture');

    const myUserId = user?.UserData?.id;
    const isProfilePage = location.pathname.startsWith(PROFILE_ROUTE);

    const isMyProfile =
        isProfilePage &&
        user?.isAuth &&
        (
            !profileIdParam || String(profileIdParam) === String(myUserId)
        );

    const navBarHeight = Math.max(Number(helper.navBarHeight || 0) - 1, 0);

    const containerStyles = {
        marginTop: isFavouritesPage ? `${navBarHeight}px` : `-1px`
    };

    const getPlacesCategoryFromPath = (pathname) => {
        if (pathname.startsWith(`${PLACES_ROUTE}/history`)) return 'history';
        if (pathname.startsWith(`${PLACES_ROUTE}/natural`)) return 'natural';
        if (pathname.startsWith(`${PLACES_ROUTE}/architecture`)) return 'architecture';
        if (pathname.startsWith(`${PLACES_ROUTE}/leisure`)) return 'leisure';

        return 'architecture';
    };

    const getPlacesCategoryPath = (category) => {
        return `${PLACES_ROUTE}/${category}`;
    };

    const getAboutAnchorTo = (hash) => ({
        pathname: ABOUT_US_ROUTE,
        hash
    });

    const scrollToHash = useCallback((hash, behavior = 'smooth', attempt = 0) => {
        const id = String(hash || '').replace(/^#/, '');

        if (!id) {
            return;
        }

        const element = document.getElementById(id);

        if (!element) {
            if (attempt < 12) {
                window.setTimeout(() => {
                    scrollToHash(hash, behavior, attempt + 1);
                }, 80);
            }

            return;
        }

        const offset = navBarHeight + 12;
        const top = element.getBoundingClientRect().top + window.pageYOffset - offset;

        window.scrollTo({
            top: Math.max(top, 0),
            left: 0,
            behavior
        });
    }, [navBarHeight]);

    useEffect(() => {
        setActivePath(location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        setPlacesMenuCategory(getPlacesCategoryFromPath(location.pathname));
    }, [location.pathname]);

    useEffect(() => {
        if (location.pathname === ABOUT_US_ROUTE && location.hash) {
            scrollToHash(location.hash, 'smooth');
        }
    }, [location.pathname, location.hash, scrollToHash]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await fetchAllPlaces();
                add.setPlaces(data.rows || []);
            } catch (error) {
                setAlertText('Ошибка при загрузке данных');
                setErrorActive(true);
            }
        };

        fetchData();
    }, [add]);

    const handleNavClick = (path, goalText = "NAVIGATION") => {
        reachGoal(`click(${path}) ${goalText}`);
    };

    const handleAboutAnchorClick = (hash) => {
        const path = `${ABOUT_US_ROUTE}${hash}`;

        reachGoal(`click(${path}) NAVIGATION`);

        if (location.pathname === ABOUT_US_ROUTE) {
            window.setTimeout(() => {
                scrollToHash(hash, 'smooth');
            }, 0);
        }
    };

    const openPlacesMenu = () => {
        if (!isPlacesMenuOpen) {
            setPlacesMenuCategory(getPlacesCategoryFromPath(location.pathname));
        }

        setIsPlacesMenuOpen(true);
    };

    const closePlacesMenu = () => {
        setIsPlacesMenuOpen(false);
    };

    const closePlacesMenuAfterClick = () => {
        setIsPlacesMenuOpen(false);

        if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
        }
    };

    const handlePlacesBlur = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsPlacesMenuOpen(false);
        }
    };

    const handlePlacesCategoryClick = (category) => {
        const path = getPlacesCategoryPath(category);

        setPlacesMenuCategory(category);
        setIsPlacesMenuOpen(false);

        reachGoal(`click(${path}) NAVIGATION`);
    };

    const renderPlacesPanelByCategory = (category) => {
        const isActive = placesMenuCategory === category;
        const groupClassName = `${styles.placesLinksGroup} ${!isActive ? styles.placesLinksGroupHidden : ''}`;

        switch (category) {
            case 'history':
                return (
                    <div className={groupClassName}>
                        <div className={styles.placesSingleLink}>
                            <Link
                                to={`${PLACES_ROUTE}/history`}
                                onClick={() => {
                                    setIsPlacesMenuOpen(false);
                                    reachGoal(`click(${PLACES_ROUTE}/history) NAVIGATION`);
                                }}
                            >
                                Перейти в раздел
                            </Link>
                        </div>
                    </div>
                );

            case 'natural':
                return (
                    <div className={groupClassName}>
                        {add.places.slice(1, 9).map(place => (
                            <div
                                key={place.id}
                                className={styles.placeLinkRow}
                                onClick={() => setIsPlacesMenuOpen(false)}
                            >
                                <PlaceNavItem place={place} />
                            </div>
                        ))}
                    </div>
                );

            case 'architecture':
                return (
                    <div className={groupClassName}>
                        {add.places.slice(9, 22).map(place => (
                            <div
                                key={place.id}
                                className={styles.placeLinkRow}
                                onClick={() => setIsPlacesMenuOpen(false)}
                            >
                                <PlaceNavItem place={place} />
                            </div>
                        ))}
                    </div>
                );

            case 'leisure':
                return (
                    <div className={groupClassName}>
                        {add.places.slice(22, 34).map(place => (
                            <div
                                key={place.id}
                                className={styles.placeLinkRow}
                                onClick={() => setIsPlacesMenuOpen(false)}
                            >
                                <PlaceNavItem place={place} />
                            </div>
                        ))}
                    </div>
                );

            default:
                return null;
        }
    };

    const renderPlacesPanels = () => (
        <>
            {renderPlacesPanelByCategory('history')}
            {renderPlacesPanelByCategory('natural')}
            {renderPlacesPanelByCategory('architecture')}
            {renderPlacesPanelByCategory('leisure')}
        </>
    );

    return (
        <>
            <div
                className={`${styles.mainMenuContainer} ${isFavouritesPage ? styles.favouritesPageMod : ''}`}
                ref={ref}
                style={containerStyles}
            >
                <div className="container-custom">
                    <div className={styles.mainMenu}>
                        <ul className={styles.subMainMenu}>
                            <li className={`${styles.menuItem} ${activePath === MAIN_ROUTE ? styles.activeMenuItem : ''}`}>
                                <Link
                                    to={MAIN_ROUTE}
                                    onClick={() => handleNavClick(MAIN_ROUTE)}
                                >
                                    Главная
                                </Link>
                            </li>

                            <li
                                className={`${styles.menuItem} ${
                                    activePath === SHOP_ROUTE || isMapSearchPage ? styles.activeMenuItem : ''
                                }`}
                            >
                                <Link
                                    to={SHOP_ROUTE}
                                    onClick={() => handleNavClick(SHOP_ROUTE)}
                                >
                                    Жилье
                                </Link>
                            </li>

                            <li
                                className={`${styles.menuItem} ${styles.menuItemPlaces} ${activePath.startsWith(PLACES_ROUTE) ? styles.activeMenuItem : ''}`}
                                onMouseEnter={openPlacesMenu}
                                onMouseLeave={closePlacesMenu}
                                onFocus={openPlacesMenu}
                                onBlur={handlePlacesBlur}
                            >
                                <Link
                                    to={PLACES_ROUTE}
                                    onClick={() => {
                                        setIsPlacesMenuOpen(false);
                                        handleNavClick(PLACES_ROUTE);
                                    }}
                                >
                                    Интересные места
                                </Link>

                                <div
                                    className={`${styles.placesMegaMenu} ${isPlacesMenuOpen ? styles.placesMegaMenuOpen : ''}`}
                                    data-prerender-remove="true"
                                    onClickCapture={(event) => {
                                        const target = event.target;

                                        if (target instanceof Element && target.closest('a')) {
                                            closePlacesMenuAfterClick();
                                        }
                                    }}
                                >
                                    <div className={styles.placesMegaInner}>
                                        <div className={styles.placesMegaLayout}>
                                            <div className={styles.placesCategories}>
                                                <Link
                                                    to={getPlacesCategoryPath('history')}
                                                    className={`${styles.placesCategoryLink} ${placesMenuCategory === 'history' ? styles.placesCategoryActive : ''}`}
                                                    onMouseEnter={() => setPlacesMenuCategory('history')}
                                                    onFocus={() => setPlacesMenuCategory('history')}
                                                    onClick={() => handlePlacesCategoryClick('history')}
                                                    aria-label="История развития города"
                                                >
                                                    История развития города
                                                </Link>

                                                <Link
                                                    to={getPlacesCategoryPath('natural')}
                                                    className={`${styles.placesCategoryLink} ${placesMenuCategory === 'natural' ? styles.placesCategoryActive : ''}`}
                                                    onMouseEnter={() => setPlacesMenuCategory('natural')}
                                                    onFocus={() => setPlacesMenuCategory('natural')}
                                                    onClick={() => handlePlacesCategoryClick('natural')}
                                                    aria-label="Природные богатства"
                                                >
                                                    Природные богатства
                                                </Link>

                                                <Link
                                                    to={getPlacesCategoryPath('architecture')}
                                                    className={`${styles.placesCategoryLink} ${placesMenuCategory === 'architecture' ? styles.placesCategoryActive : ''}`}
                                                    onMouseEnter={() => setPlacesMenuCategory('architecture')}
                                                    onFocus={() => setPlacesMenuCategory('architecture')}
                                                    onClick={() => handlePlacesCategoryClick('architecture')}
                                                    aria-label="Архитектура и искусство"
                                                >
                                                    Архитектура и искусство
                                                </Link>

                                                <Link
                                                    to={getPlacesCategoryPath('leisure')}
                                                    className={`${styles.placesCategoryLink} ${placesMenuCategory === 'leisure' ? styles.placesCategoryActive : ''}`}
                                                    onMouseEnter={() => setPlacesMenuCategory('leisure')}
                                                    onFocus={() => setPlacesMenuCategory('leisure')}
                                                    onClick={() => handlePlacesCategoryClick('leisure')}
                                                    aria-label="Досуг"
                                                >
                                                    Досуг
                                                </Link>
                                            </div>

                                            <div className={styles.placesLinksPanel}>
                                                {renderPlacesPanels()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>

                            <li className={`${styles.menuItem} ${activePath === PHOTO_GALLERY_ROUTE ? styles.activeMenuItem : ''}`}>
                                <Link
                                    to={PHOTO_GALLERY_ROUTE}
                                    onClick={() => handleNavClick(PHOTO_GALLERY_ROUTE)}
                                >
                                    Фотогалерея
                                </Link>
                            </li>

                            <li className={`${styles.menuItem} ${activePath === BLOG_ROUTE ? styles.activeMenuItem : ''}`}>
                                <Link
                                    to={BLOG_ROUTE}
                                    onClick={() => handleNavClick(BLOG_ROUTE)}
                                >
                                    Блог
                                </Link>
                            </li>

                            <li className={`${styles.menuItem} ${activePath.startsWith(ABOUT_US_ROUTE) ? styles.activeMenuItem : ''}`}>
                                <Link
                                    to={ABOUT_US_ROUTE}
                                    onClick={() => handleNavClick(ABOUT_US_ROUTE)}
                                >
                                    О нас
                                </Link>

                                <div
                                    className={styles.submenuWrap}
                                    data-prerender-remove="true"
                                >
                                    <ul className={styles.submenu}>
                                        <li>
                                            <Link
                                                to={getAboutAnchorTo('#info-section')}
                                                aria-label="Информация"
                                                onClick={() => handleAboutAnchorClick('#info-section')}
                                            >
                                                Информация
                                            </Link>
                                        </li>

                                        <li>
                                            <Link
                                                to={getAboutAnchorTo('#roadmap-section')}
                                                aria-label="Дорожная карта"
                                                onClick={() => handleAboutAnchorClick('#roadmap-section')}
                                            >
                                                Дорожная карта
                                            </Link>
                                        </li>

                                        <li>
                                            <Link
                                                to={getAboutAnchorTo('#legal-info-section')}
                                                aria-label="Правовая информация"
                                                onClick={() => handleAboutAnchorClick('#legal-info-section')}
                                            >
                                                Правовая информация
                                            </Link>
                                        </li>

                                        <li>
                                            <Link
                                                to={getAboutAnchorTo('#contacts-section')}
                                                aria-label="Контакты"
                                                onClick={() => handleAboutAnchorClick('#contacts-section')}
                                            >
                                                Контакты
                                            </Link>
                                        </li>

                                        <li>
                                            <Link
                                                to={getAboutAnchorTo('#tariffs-section')}
                                                aria-label="Тарифы"
                                                onClick={() => handleAboutAnchorClick('#tariffs-section')}
                                            >
                                                Тарифы
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </li>
                        </ul>

                        <div
                            className={styles.selectionButtonWrapper}
                            data-prerender-remove="true"
                        >
                            <div className={styles.mobileExploreWrapper}>
                                {(() => {
                                    const path = location.pathname;

                                    if (path === SHOP_ROUTE) {
                                        return null;
                                    }

                                    const showBurger =
                                        path === MAIN_ROUTE ||
                                        (path.startsWith(PROFILE_ROUTE) && isMyProfile);

                                    if (showBurger) {
                                        return (
                                            <button
                                                type="button"
                                                className={styles.exploreTrigger}
                                                onClick={() => setIsExploreOpen(true)}
                                            >
                                                <div className={styles.burgerIcon}>
                                                    <img
                                                        src={burgerMenu}
                                                        alt="Меню"
                                                        className={styles.exploreIcon}
                                                    />
                                                </div>
                                            </button>
                                        );
                                    }

                                    return (
                                        <button
                                            type="button"
                                            className={styles.exploreTrigger}
                                            onClick={() => history.goBack()}
                                        >
                                            <div className={styles.burgerIcon}>
                                                <img
                                                    src={backArrow}
                                                    alt="Назад"
                                                    className={styles.exploreIcon}
                                                    style={{ width: '24px' }}
                                                />
                                            </div>
                                        </button>
                                    );
                                })()}
                            </div>

                            <button
                                type="button"
                                className={styles.selectionButton}
                                onClick={() => {
                                    setFormSelectionVisible(true);
                                    reachGoal("setFormSelectionVisible(true) NAVIGATION");
                                }}
                            >
                                <span className={styles.buttonTextSmall}>Бесплатный подбор</span>
                                <span className={styles.buttonTextBig}>Бесплатный подбор жилья</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div data-prerender-remove="true">
                <ModalPortal>
                    <FormSelection show={formSelectionVisible} onHide={() => setFormSelectionVisible(false)} />
                </ModalPortal>

                <ModalPortal>
                    <ExploreMenu show={isExploreOpen} onClose={() => setIsExploreOpen(false)} />
                </ModalPortal>

                <ErrorForm alert={alertText} show={errorActive} onHide={() => setErrorActive(false)} />
            </div>
        </>
    );
}));

export default Navigation;